import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import * as routes from '@app/routes';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Subject } from 'rxjs';
import { Observable } from 'rxjs';
import { LoaderService } from '@app/services/loader.service';
import { select, Store } from '@ngrx/store';
import {
  getCampaignError,
  getInfluencerError,
  getInfluencers,
  ICampaignState,
  IInfluencersState,
  IMailVerifyState,
  InfluencersListByProject,
  CampaignValidation,
  CampaignValidationSuccess,
  getCampaignValidation
} from '@app/stores';
import { takeUntil } from 'rxjs/operators';
import { LaunchScheduleModalComponent } from '../launch-schedule-modal/launch-schedule-modal.component';
import moment from 'moment-timezone';
import {Constants, smartDateFormat} from '@app/consts';
import { find, isEmpty } from 'lodash';
import { BehaviorSubjectHelper } from '@app/classes/behavior-subject-helper';
import { CampaignService } from '@app/services/campaign.service';
import { TemplateGeneratorService } from '@app/services/template-generator.service';
import { MailVerifyService } from '@app/services/mail-verify.service';
import { LaunchConfirmationModalComponent } from '@app/modules/launch-confirmation-modal/launch-confirmation-modal.component';
import {InfluencerApiService} from '@app/services/api/influencer-api.service';
import {VerificationComponent} from '@app/_components/verification/verification.component';
import {RegistrationPlatformService} from '@app/services/registration-platform.service';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { StatusBarComponent } from '../../status-bar/status-bar.component';
import { InfoPopoverComponent } from '../../../_components/info-popover/info-popover.component';
import { AsyncPipe } from '@angular/common';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';

//
// @TODO: add this in a separate file to reuse
interface ScheduleDate {
  schedule_date?: string;
  schedule_time?: string;
  timezone?: any;
  timezoneValue?: any;
  timezoneText?: any;
  sync_influencers_before_launch?: boolean;
  require_successful_sync?: boolean;
  sync_hours_before_launch?: number;
  sync_registration_platform?: string;
  sync_project_registration_platform_id?: number;
  sync_influencers_after_launch?: boolean;
  sync_after_launch_registration_platform_id?: number;
}

@Component({
  selector: 'app-launch',
  templateUrl: './launch-campaign.component.html',
  styleUrls: ['./launch-campaign.component.scss'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, StatusBarComponent, InfoPopoverComponent, RouterLink, AsyncPipe]
})
export class LaunchCampaignComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private activatedRoute = inject(ActivatedRoute);
  private loaderService = inject(LoaderService);
  private influencerStore = inject<Store<IInfluencersState>>(Store);
  private campaignStore = inject<Store<ICampaignState>>(Store);
  private mailVerifyStore = inject<Store<IMailVerifyState>>(Store);
  private readonly sbModal = inject(SbModalService);
  private readonly constants = inject(Constants);
  private readonly campaignService = inject(CampaignService);
  private influencersApiService = inject(InfluencerApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly templateGeneratorService = inject(TemplateGeneratorService);
  private readonly mailVerifyService = inject(MailVerifyService);
  private readonly registrationPlatformService = inject(RegistrationPlatformService);
  // The page's many plain fields are written from the store, the launch request and the
  // dialogs' promises: each of those marks the view.
  private readonly changeDetector = inject(ChangeDetectorRef);

  public campaign: BehaviorSubjectHelper<any> = new BehaviorSubjectHelper<any>({});
  public step;
  public influencerCount: any;
  public newInfluencerCount: any;
  public campaignEmailInfluencerCount: any;
  routes = routes;

  public doTheSegmentHaveInfluencers: boolean;
  public doTheSegmentHaveInfluencersLoading: boolean;
  public isCampaignTemplateSet: boolean;
  public isCampaignTemplateSetLoading: boolean;
  public isLandingPageSet: boolean;
  public isLandingPageSkipped: boolean;
  public isLandingPageSetLoading: boolean;
  public isSocialPageSet: boolean;
  public isSocialPageSetLoading: boolean;
  public launchStatus: string;
  public influencerType: string;
  public showEmailCustomize = false;
  public schedule: ScheduleDate = {};
  unsubscriber = new Subject();
  loader$: Observable<boolean>;
  public campaignMeta: any = {};
  resendEmailClick = false;
  timeZoneValues = [];
  currentUser = JSON.parse(localStorage.getItem('currentUser'));
  projectId = '';
  campaignId = '';
  public launchType = '';
  public allow_campaign_to_launch_when_registration_widget_is_disabled: boolean;
  public isWidgetActive: boolean;
  public isWidgetPublicAccess: boolean;
  public widget_Settings;
  public hasActiveRegistrationPlatforms = false;
  public hasPushRegistrationPlatforms = false;
  public registrationPlatforms: any[] = [];

  get totalRelaunchInfluencerCount(): number {
    return +this.newInfluencerCount + +this.campaignEmailInfluencerCount;
  }

  get sendsEmail(): boolean {
    return this.campaign.value?.email_disabled !== 'true';
  }

  constructor() {
    const constants = this.constants;

    this.doTheSegmentHaveInfluencers = false;
    this.doTheSegmentHaveInfluencersLoading = true;
    this.isCampaignTemplateSet = false;
    this.isCampaignTemplateSetLoading = true;
    this.isLandingPageSet = false;
    this.isLandingPageSkipped = false;
    this.isLandingPageSetLoading = true;
    this.isSocialPageSet = false;
    this.isSocialPageSetLoading = true;
    this.launchStatus = null;
    this.influencerCount = 0;
    this.newInfluencerCount = 0;
    this.campaignEmailInfluencerCount = 0;
    this.timeZoneValues = constants.timezone.map(x => x.value);
    this.campaignStore.dispatch(CampaignValidationSuccess(null));
    this.subscribeStore();
    this.activatedRoute.queryParams.subscribe(async (queryParams) => {
      if (queryParams.type) {
        this.launchType = queryParams.type;
        this.changeDetector.markForCheck();
      }
    });
    this.projectId = this.route.snapshot.parent.params.projectId;
    this.campaignId = this.route.snapshot.parent.params.campaignId;
  }

  subscribeStore() {
    // this.influencerStoreSubscribe();
    this.campaignStoreSubscribe();
  }

  campaignStoreSubscribe() {
    this.campaignStore
      .pipe(select(getCampaignError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((error) => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });

    this.campaignStore
      .pipe(select(getCampaignValidation))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((data) => {
        if (data) {
          this.influencerCount = data.influencerCount;
          this.newInfluencerCount = data.newInfluencerCount;
          this.campaignEmailInfluencerCount = data.campaignEmailInfluencerCount;
          this.influencerType = data.influencerType;
          this.doTheSegmentHaveInfluencers = data.doTheSegmentHaveInfluencers;
          this.isLandingPageSet = data.isLandingPageSet;
          this.isLandingPageSkipped = data.isLandingPageSkipped;
          this.isSocialPageSet = data.isSocialPageSet;
          this.showEmailCustomize = data.showEmailCustomize;
          this.isCampaignTemplateSet = data.isCampaignTemplateSet;
          this.campaign.set(data.campaign);
          this.campaignMeta = {};

          if (data.campaign.status !== 'draft') {
            this.launchStatus = 'done';
          }
          if (data.campaign.meta) {
            this.campaignMeta = typeof data.campaign.meta === 'string' ? {...JSON.parse(data.campaign.meta)} : {...data.campaign.meta};
          }
          this.allow_campaign_to_launch_when_registration_widget_is_disabled = this.campaignMeta.allow_campaign_to_launch_when_registration_widget_is_disabled;
          this.widget_Settings = typeof data.campaign.project.widget_settings === 'string' ? {...JSON.parse(data.campaign.project.widget_settings)} : {...data.campaign.project.widget_settings};
          if(this.widget_Settings.active) {
            this.isWidgetActive = true;
            if(this.widget_Settings.whitelist_access) {
              this.isWidgetPublicAccess = true;
            }
          }
          this.doTheSegmentHaveInfluencersLoading = false;
          this.isCampaignTemplateSetLoading = false;
          this.isLandingPageSetLoading = false;
          this.isSocialPageSetLoading = false;
          this.setStatusBar();
          this.changeDetector.markForCheck();
        }
      });

    // this.campaignStore.pipe(select(getCampaignLaunch))
    //   .pipe(takeUntil(this.unsubscriber))
    //   .subscribe(campaign => {
    //     if (campaign) {
    //       if (campaign.status !== 'draft') {
    //         //
    //         // Update campaign object
    //         this.campaign.set(campaign);
    //         // update page status and status bar
    //         this.launchStatus = 'done';
    //         this.setStatusBar();
    //       }
    //     }
    //   });
  }

  // influencerStoreSubscribe() {
  //   this.influencerStore
  //     .pipe(select(getInfluencerError))
  //     .pipe(takeUntil(this.unsubscriber))
  //     .subscribe((error) => {
  //       if (error) {
  //         this.toastr.error(error, 'Error');
  //       }
  //     });
  //   this.influencerStore
  //     .pipe(select(getInfluencers))
  //     .pipe(takeUntil(this.unsubscriber))
  //     .subscribe((influencers) => {
  //       if (influencers && influencers.length > 0) {
  //         this.filterInfluencers(influencers);
  //       } else {
  //         this.influencerCount = 0;
  //         this.doTheSegmentHaveInfluencers = false;
  //         this.doTheSegmentHaveInfluencersLoading = false;
  //       }
  //     });
  // }

  /*influencerStoreSubscribe() {
    if (this.campaign.value?.id) {
      const params = {
        project_id: this.campaign.value?.project_id,
        campaign_id: this.campaign.value?.id,
        project_influencer_segment_id: this.campaign.value?.segments[0].id
      };
      this.influencersApiService.getInfluencerCount(params).subscribe(response => {
        if (response.body.success) {
          if (response.body.data > 0) {
            this.influencerCount = response.body.data;
            this.doTheSegmentHaveInfluencers = this.influencerCount > 0;
            this.doTheSegmentHaveInfluencersLoading = false;
          } else {
            this.influencerCount = 0;
            this.doTheSegmentHaveInfluencers = false;
            this.doTheSegmentHaveInfluencersLoading = false;
          }
        }
      });
    } else {
      setTimeout(() => {
        this.influencerStoreSubscribe();
      }, 1000);
      return;
    }
  }*/

  // filterInfluencers(influencers) {
  //   if (this.campaign.value?.id) {
  //     const segment = this.campaign.value?.segments[0];
  //     const filteredInfluencers = influencers.filter((influencer) => {
  //       return influencer.project_influencer_segment_id === segment.id;
  //     });
  //     this.influencerCount = filteredInfluencers.length;
  //     this.doTheSegmentHaveInfluencers = this.influencerCount > 0;
  //     this.doTheSegmentHaveInfluencersLoading = false;
  //   } else {
  //     setTimeout(() => {
  //       this.filterInfluencers(influencers);
  //     }, 1000);
  //     return;
  //   }
  // }

  ngOnInit() {
    this.loader$ = this.loaderService.loader$;
    const projectId = this.route.snapshot.parent.params.projectId;

    this.campaignStore.dispatch(CampaignValidation({
      project_id: projectId,
      campaign_id: this.route.snapshot.parent.params.campaignId,
    }));

    // Fetch registration platforms for project
    this.registrationPlatformService.listForProject(projectId).subscribe((response: any) => {
      if (response?.success && response?.data) {
        this.hasActiveRegistrationPlatforms = response.data.hasActiveRegistrationPlatforms || false;
        this.hasPushRegistrationPlatforms = !!response.data.hasPushRegistrationPlatforms;
        this.registrationPlatforms = response.data.registrationPlatforms || [];
      }
    });
    // this.setCampaign();
  }

  setStatusBar() {
    this.step = {
      title: this.launchType !== 'relaunch' ? 'Launch' : 'ReLaunch',
      params: {
        projectId: this.campaign.value?.project_id,
        campaignId: this.campaign.value?.id,
        campaignName: this.campaign.value?.name,
        isLaunched: this.launchStatus === 'done' && this.launchType !== 'relaunch',
      },
    };
  }

  async setCampaign() {
    try {
      //
      // Get campaign
      const campaignFromNetwork: any = await lastValueFrom(this.campaignService
        .getCampaign(this.route.snapshot.parent.params.campaignId, {
          options: JSON.stringify({ include_segments: true, include_project: true }),
        }));

      //
      // Validate campaign
      if (!campaignFromNetwork?.data?.id) {
        // eslint-disable-next-line no-throw-literal -- the catch below tells this case apart by the string
        throw 'Campaign not found';
      }
      // set campaign
      const campaign: any = campaignFromNetwork.data;
      // unset loading
      this.loaderService.hide();

      //
      // Set campaign entry
      this.campaign.set(campaign);

      //
      // Set campaign status
      if (campaign.status !== 'draft') {
        this.launchStatus = 'done';
      }

      //
      // Set meta
      if (campaign.meta) {
        this.campaignMeta = typeof campaign.meta === 'string' ? { ...JSON.parse(campaign.meta) } : { ...campaign.meta };
      }

      //
      // Set customized email
      if (!this.campaignMeta.hasOwnProperty('email_customized')) {
        this.campaignMeta.email_customized = false;
      }

      //
      // Validate campaign segments
      if (!this.campaign.value?.segments?.length) {
        this.router.navigate([`/project/${this.campaign.value?.project_id}/add-influencer`], {
          queryParams: { campId: this.campaign.value?.id },
        });
        return;
      }

      // this.influencerStore.dispatch(
      //   InfluencersListByProject({ projectId: this.campaign.value?.project_id, campaignId: this.campaign.value?.id,
      //     options: JSON.stringify({
      //       onlyCount: true
      //     })
      //   })
      // );

      this.influencerType = this.campaign.value?.segments[0].influencer_type.name;
      this.isLandingPageSet = true;
      this.isSocialPageSet = true;
      if (this.campaign.value?.email_from && this.campaignMeta.email_customized) {
        this.showEmailCustomize = true;
        this.validateMailAddressOnServer();
        // this.mailVerifyStore.dispatch(MailVerifySenderStatus({ params: { identity: this.campaign.value?.email_from } }));
      }
      this.isLandingPageSetLoading = false;
      this.isSocialPageSetLoading = false;
      this.setStatusBar();
    } catch (error) {
      console.log('error', error);
      this.loaderService.hide();
      this.toastr.error(error, 'Error');

      if (error === 'Campaign not found') {
        this.router.navigate([`/projects/${this.route.snapshot.parent.params.projectId}/campaigns`]);
      }
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  // subscribeToCampaign() {
  //   this.templateGeneratorService.onCampaignLoaded
  //     .pipe(takeUntil(this.unsubscriber))
  //     .subscribe(async campaign => {
  //       if (!campaign) {
  //         return;
  //       }

  //       this.campaign.set(campaign);
  //       console.log('campaign.status', campaign.status);
  //       if (campaign.status !== 'draft') {
  //         this.launchStatus = 'done';
  //       }
  //       if (campaign.meta) {
  //         this.campaignMeta = (typeof campaign.meta === 'string') ? { ...JSON.parse(campaign.meta) } : { ...campaign.meta };
  //       }
  //       if (!this.campaignMeta.hasOwnProperty('email_customized')) {
  //         this.campaignMeta.email_customized = false;
  //       }
  //       if (!this.campaign.value?.segments.length) {
  //         this.router.navigate([`/project/${this.campaign.value?.project_id}/add-influencer`], { queryParams: { campId: this.campaign.value?.id } });
  //         return;
  //       }

  //       this.influencerStore.dispatch(InfluencersListByProject({ projectId: this.campaign.value?.project_id, campaignId: this.campaign.value?.id }));

  //       this.influencerType = this.campaign.value?.segments[0].influencer_type.name;
  //       this.isLandingPageSet = true;
  //       this.isSocialPageSet = true;
  //       if (this.campaign.value?.email_from && this.campaignMeta.email_customized) {
  //         this.showEmailCustomize = true;
  //         this.mailVerifyStore.dispatch(MailVerifySenderStatus({ params: { identity: this.campaign.value?.email_from } }));
  //       }
  //       this.isLandingPageSetLoading = false;
  //       this.isSocialPageSetLoading = false;
  //       this.setStatusBar();
  //     });
  // }

  redirect(type) {
    switch (type) {
      case 'template':
        return this.router.navigateByUrl(
          routes.campaignTemplate({
            projectId: this.projectId,
            campaignId: this.campaignId,
          })
        );
      case 'socialShare':
        return this.router.navigate([`/projects/${this.projectId}/campaigns/${this.campaignId}/share`]);
      case 'statistics':
        return this.router.navigate([`/projects/${this.projectId}/statistics`], {
          queryParams: { campaignId: this.campaignId },
        });
      case 'influencer':
        return this.router.navigate([`/project/${this.projectId}/add-influencer`], {
          queryParams: { campId: this.campaignId },
        });
      case 'email':
        return this.router.navigateByUrl(
          routes.campaignPreview({
            projectId: this.projectId,
            campaignId: this.campaignId,
          })
        );
      case 'widget':
        return this.router.navigate([`/projects/${this.projectId}/widget`]);
    }
  }

  async launchCampaign(code: string, options: any = {}) {
    //
    // Start launch
    this.launchStatus = 'start';
    // Called from the confirmation dialog's promise, not from an event of this view.
    this.changeDetector.markForCheck();

    try {
      //
      // Launch campaign
      const campaignLaunch: any =
        options.schedule && options.timezone
          ? await lastValueFrom(this.campaignService
            .launchScheduled(
              this.campaign.value?.id,
              options.schedule,
              options.timezone,
              code,
              this.launchType === 'relaunch',
              options.sync_influencers_before_launch,
              options.sync_hours_before_launch,
              options.require_successful_sync,
              options.sync_registration_platform,
              {
                sync_project_registration_platform_id: options.sync_project_registration_platform_id,
                sync_influencers_after_launch: options.sync_influencers_after_launch,
                sync_after_launch_registration_platform_id: options.sync_after_launch_registration_platform_id,
              }
            ))
          : await lastValueFrom(this.campaignService.launch(this.campaign.value?.id, code , this.launchType === 'relaunch'));

      // validate
      if (!campaignLaunch.success || !campaignLaunch?.data?.id) {
        this.loaderService.hide(true);
        return this.toastr.error(this.loaderService.getErrorMessage(campaignLaunch), 'Error');
      }

      //
      // Update campaign if this is not a draft
      if (campaignLaunch?.data?.status !== 'draft') {
        //
        // Update campaign object
        this.launchType = '';
        this.campaign.set(campaignLaunch?.data);
        // update page status and status bar
        this.launchStatus = 'done';
        this.setStatusBar();
        this.launchCampaignRoute(this.campaignId);
      }
    } catch (error) {
      //
      // Start launch
      this.launchStatus = null;

      this.loaderService.hide(true);
      return this.toastr.error(this.loaderService.getErrorMessage(error), 'Error');
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  async onSaveButtonClick() {
    this.router.navigateByUrl('/' + routes.campaigns({ projectId: this.campaign.value?.project_id }));
  }

  resendMail() {
    this.resendEmailClick = true;
    this.validateMailAddressOnServer(true);
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
    // this.templateGeneratorService.unsubscribe();
  }

  /** The launch dialogs are told the launch type only for a relaunch, and keep their own otherwise. */
  private relaunchInput(): {launchType?: string} {
    return this.launchType === 'relaunch' ? {launchType: this.launchType} : {};
  }

  openLaunchScheduleModal(data: ScheduleDate = {}) {
    //
    // Unset loader
    this.loader$ = null;

    //
    // add current project.timezone to data.timezone if available and not set yet
    if (!data?.timezone && this.campaign.value?.project?.timezone) {
      data.timezone = this.campaign.value?.project.timezone;
    }

    //
    // Open modal, with the current schedule dates
    const modelRef = this.sbModal.openComponent(LaunchScheduleModalComponent, {
      centered: false,
      // size: 'sm',
      keyboard: true,
      windowClass: 'schedule-modal',
    }, {
      schedule: data,
      hasActiveRegistrationPlatforms: this.hasActiveRegistrationPlatforms,
      hasPushRegistrationPlatforms: this.hasPushRegistrationPlatforms,
      registrationPlatforms: this.registrationPlatforms,
      ...this.relaunchInput(),
    });

    //
    // On close modal event
    // should validate and update schedule date
    modelRef.result.then((result: ScheduleDate = {}) => {
      //
      // Validate result
      if (!result?.schedule_date || !result?.schedule_time) {
        return;
      }
      // update schedule date
      this.schedule = { ...result };
      this.changeDetector.markForCheck();

      this.openLaunchConfirmationModal();
    });
  }

  openLaunchScheduleConfirmationModal() {
    //
    // Unset loader
    this.loader$ = null;

    //
    // Open modal, with the current schedule dates
    const modelRef = this.sbModal.openComponent(LaunchConfirmationModalComponent, {
      centered: false,
      size: 'md',
      keyboard: true,
    }, {
      schedule: this.schedule,
      currentUser: this.currentUser,
      timeZoneValues: this.timeZoneValues,
      campaignID: this.campaign.value?.id,
      ...this.relaunchInput(),
    }, {
      scheduleCleared: (schedule) => {
        this.schedule = schedule;
        this.changeDetector.markForCheck();
      },
    });

    //
    // On close modal event
    // should validate and update schedule date
    modelRef.result.then((result: { action: 'edit' | 'launch' | 'schedule' | 'schedule-launch' | 'clear' | 'launch-now'; code?: string }): any => {
      // Every branch below writes the schedule, if anything, before it returns.
      this.changeDetector.markForCheck();
      //
      // action = edit: open edit dialog
      if (result.action === 'edit') {
        return this.openLaunchScheduleModal(this.schedule);
      } else if (result.action === 'schedule-launch' && result?.code) {
        return this.launchCampaign(result.code, {
          schedule: this.schedule.schedule_date + ' ' + this.schedule.schedule_time,
          timezone: this.schedule?.timezone,
          sync_influencers_before_launch: this.schedule?.sync_influencers_before_launch,
          sync_hours_before_launch: this.schedule?.sync_hours_before_launch,
          require_successful_sync: this.schedule?.require_successful_sync,
          sync_registration_platform: this.schedule?.sync_registration_platform,
          sync_project_registration_platform_id: this.schedule?.sync_project_registration_platform_id,
          sync_influencers_after_launch: this.schedule?.sync_influencers_after_launch,
          sync_after_launch_registration_platform_id: this.schedule?.sync_after_launch_registration_platform_id
        });
      } else if (result.action === 'launch' && result?.code) {
        this.schedule = {};
        return this.launchCampaign(result.code);
      } else if (result.action === 'launch-now') {
        return this.launchCampaign(result.code);
      } else if (result.action === 'clear') {
        this.schedule = {};
        return true;
      } else if (result.action === 'schedule') {
        this.schedule = {};
        return this.launchScheduledCampaign();
      }
    }).catch(() => undefined); // Esc, a click outside, or no launch code: the dialog was dismissed.
  }

  openLaunchConfirmationModal() {
    //
    // Unset loader
    this.loader$ = null;

    if (!isEmpty(this.schedule)) {
      return this.openLaunchScheduleConfirmationModal();
    }

    //
    // Open modal
    const modelRef = this.sbModal.openComponent(LaunchConfirmationModalComponent, {
      centered: false,
      // size: 'sm',
      keyboard: true,
    }, {
      schedule: this.schedule || {},
      campaignID: this.campaign.value?.id,
      ...this.relaunchInput(),
    });
    //
    // On close modal event
    // should validate and update schedule date
    modelRef.result.then((result: { action: 'cancel' | 'launch' | 'schedule'; code?: string }): any => {
      //
      // action = edit: open edit dialog
      if (result.action === 'cancel' || !result?.code) {
        return;
      } else if (result.action === 'launch') {
        // action  = launch: should launch the scheduled campaign
        return this.launchCampaign(result.code);
      } else if (result.action === 'schedule') {
        return this.launchScheduledCampaign();
      } else if (result.action === 'edit') {
        this.schedule = {};
        this.changeDetector.markForCheck();
      }
    }).catch(() => undefined); // Esc, a click outside, or no launch code: the dialog was dismissed.
  }

  launchScheduledCampaign() {
    //
    // If schedule is not setup, open schedule dialog
    if (!this.schedule?.schedule_date || !this.schedule?.schedule_time) {
      return this.openLaunchScheduleModal();
    } else {
      // filled, open options dialog
      return this.openLaunchScheduleConfirmationModal();
    }
  }

  getScheduleDateTime(actual = false) {
    return smartDateFormat(this.schedule?.schedule_date + ' ' + this.schedule?.schedule_time, this.schedule?.timezone, null, actual ? 'fullDateTimeWithTimeZone' : 'fullDateTime', actual ? this.schedule?.timezone : null);
  }

  getScheduleDateTimeCustom(date, customTz = null, actual = false) {
    // console.log(date, customTz, actual);
    return smartDateFormat(date, customTz, null, actual ? 'fullDateTimeWithTimeZone' : 'fullDateTime', actual ? this.schedule?.timezone : null);
  }

  getLaunchedMessage() {
    //
    // Default message
    let value = `Your ${this.campaign.value?.name} campaign is now launched.`;

    // message for campaign in queue
    if (this.campaign.value?.status === 'launch_queued') {
      value = `Your ${this.campaign.value?.name} campaign is now in queue.`;
    }
    // message for campaign failed
    if (this.campaign.value?.status === 'launch_failed') {
      value = `Your ${this.campaign.value?.name} campaign has failed to launch.`;
    }

    // Change value for scheduled
    if (this.campaign.value?.status === 'launch_scheduled') {
      value = `Your ${this.campaign.value?.name} campaign is scheduled to launch at ${this.getScheduleDateTimeCustom(this.campaign.value?.launch_scheduled_at, this.campaign.value?.timezone, false)}.`;
    }

    return value;
  }

  getCampaignTZDate() {
    return smartDateFormat(this.campaign.value?.launch_scheduled_at, null, null, 'fullDateTimeWithTimeZone', this.campaign.value?.timezone);
  }

  async validateMailAddressOnServer(showModal: boolean = false) {
    //
    // Validate email_form
    if (!this.campaign.value?.email_from) {
      this.isCampaignTemplateSetLoading = false;
      this.isCampaignTemplateSet = false;
    }

    //
    // Show loading
    this.loaderService.show();
    this.isCampaignTemplateSetLoading = true;
    this.isCampaignTemplateSet = false;

    try {
      //
      // Validate email
      const emailFrom = this.campaign.value?.email_from || '';
      const domain = emailFrom.includes('@') ? emailFrom.split('@')[1] : emailFrom;
      const response: any = await lastValueFrom(this.mailVerifyService.verifySenderStatus({ identity: domain }));

      // validate
      if (response?.success !== true) {
        throw new Error('Failed to validate "From Email", try again');
      }
      const data = response.data;

      //
      // Set status
      const status: 'verified' | 'unverified' = data?.identity?.verification_status === 'verified' ? 'verified' : 'unverified';
      this.isCampaignTemplateSet = status === 'verified' ? true : false;

      const modelRef = this.sbModal.openComponent(VerificationComponent, {centered: true, keyboard: true, size: 'lg'}, {
        fromEmailVerified: status,
        emailSenderVerifyBy: 'domain',
        fromEmail: domain,
        emailSenderServiceProviderSlug: this.campaign.value?.service_provider_slug,
        mailServiceProviders: [],
        currentUser: this.currentUser,
      });
      modelRef.result.then((result) => {
      }, (reason) => {
      });
    } catch (error) {
      this.toastr.error(this.loaderService.getErrorMessage(error), 'Error');
    }

    //
    // Hide loading
    this.loaderService.hide();
    this.isCampaignTemplateSetLoading = false;
    this.changeDetector.markForCheck();
  }

  launchCampaignRoute(campaignId) {
    this.router.navigateByUrl(
      routes.launchCampaign({
        projectId: this.projectId,
        campaignId
      })
    );
  }
}
